import React, { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Send } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { openAuthDialog } from "../store/auth/authSlice";
import { Formik } from "formik";
import * as Yup from "yup";
import { submitPartInquiry } from "../store/partInquiry/partInquiryThunk";

// ✅ US ZIP Code Regex (5 digits or ZIP+4)
const postalCodeRegex = /^\d{5}(-\d{4})?$/;

const inquiryPartValidationSchema = Yup.object().shape({
  partName: Yup.string().trim().required("Part's name is required"),
  vehicleMake: Yup.string().trim().required("Vehicle Make is required"),
  vehicleModel: Yup.string().trim().required("Vehicle Model is required"),
  vehicleYear: Yup.string()
    .matches(/^\d{4}$/, "Enter a valid 4-digit year")
    .test(
      "year-range",
      "Year must be between 1976 and current year",
      (value) => {
        if (!value) return false;
        const year = parseInt(value, 10);
        const currentYear = new Date().getFullYear();
        return year >= 1976 && year <= currentYear;
      }
    )
    .required("Vehicle Year is required"),
  postalCode: Yup.string()
    .matches(
      postalCodeRegex,
      "Enter a valid US ZIP Code (e.g., 90210 or 12345-6789)"
    )
    .required("Postal code is required"),
  message: Yup.string().trim().max(300, "Message cannot exceed 300 characters")
});

const PartInquiryFormPage = () => {
  const location = useLocation();
  const { query } = location.state || {};
  const navigate = useNavigate();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const initialValues = {
    partName: query || "",
    vehicleMake: "",
    vehicleModel: "",
    vehicleYear: "",
    postalCode: "",
    message: ""
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (!isAuthenticated || !user) {
      toast({
        title: "Login Required",
        description: "Please login or sign up to send an inquiry.",
        variant: "destructive"
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname
        })
      );
      return;
    }

    dispatch(
      submitPartInquiry({
        formData: values,
        buyerId: user?.uid,
        role: user?.role
      })
    );
    toast({
      title: "Inquiry Sent!",
      description:
        "Your part request has been sent to premium sellers. They will contact you if they have a match."
    });
    resetForm();
    navigate("/");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  useEffect(() => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login or sign up to send an inquiry to Sellers.",
        variant: "destructive"
      });
    }
  }, []);

  return (
    <motion.div
      className="w-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="mb-5 md:mb-8">
        <Link
          to="/"
          className="inline-flex items-center text-primary hover:underline md:mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
        </Link>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Card className="max-w-2xl mx-auto shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-[23px] xs:text-3xl font-bold bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
              Submit a Part Inquiry
            </CardTitle>
            <CardDescription>
              Can't find the part you need? Fill out this form, and we'll notify
              our premium sellers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Formik
              initialValues={initialValues}
              validationSchema={inquiryPartValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                values,
                isSubmitting,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit
              }) => (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Part Name */}
                  <div>
                    <Label htmlFor="partName">Part Name / Description *</Label>
                    <Input
                      id="partName"
                      name="partName"
                      placeholder="e.g., Alternator, Passenger side headlight"
                      value={values.partName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.partName && touched.partName && (
                      <p className="text-xs text-red-400">{errors.partName}</p>
                    )}
                  </div>

                  {/* Vehicle Make */}
                  <div>
                    <Label htmlFor="vehicleMake">Vehicle Make *</Label>
                    <Input
                      id="vehicleMake"
                      name="vehicleMake"
                      placeholder="e.g., Honda"
                      value={values.vehicleMake}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.vehicleMake && touched.vehicleMake && (
                      <p className="text-xs text-red-400">
                        {errors.vehicleMake}
                      </p>
                    )}
                  </div>

                  {/* Vehicle Model */}
                  <div>
                    <Label htmlFor="vehicleModel">Vehicle Model *</Label>
                    <Input
                      id="vehicleModel"
                      name="vehicleModel"
                      placeholder="e.g., Civic"
                      value={values.vehicleModel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.vehicleModel && touched.vehicleModel && (
                      <p className="text-xs text-red-400">
                        {errors.vehicleModel}
                      </p>
                    )}
                  </div>

                  {/* Vehicle Year */}
                  <div>
                    <Label htmlFor="vehicleYear">Vehicle Year *</Label>
                    <Input
                      id="vehicleYear"
                      name="vehicleYear"
                      type="text"
                      placeholder="e.g., 2018"
                      value={values.vehicleYear}
                      onChange={(e) => {
                        // ✅ Only allow numbers
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleChange({
                          target: { name: "vehicleYear", value: val }
                        });
                      }}
                      onBlur={handleBlur}
                      maxLength={4}
                    />
                    {errors.vehicleYear && touched.vehicleYear && (
                      <p className="text-xs text-red-400">
                        {errors.vehicleYear}
                      </p>
                    )}
                  </div>

                  {/* Postal Code */}
                  <div>
                    <Label htmlFor="postalCode">Your Postal Code *</Label>
                    <Input
                      id="postalCode"
                      name="postalCode"
                      placeholder="e.g., 90210"
                      value={values.postalCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.postalCode && touched.postalCode && (
                      <p className="text-xs text-red-400">
                        {errors.postalCode}
                      </p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message">
                      Additional Details (Optional)
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={3}
                      placeholder="Any specific requirements or notes for the seller..."
                      value={values.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.message && touched.message && (
                      <p className="text-xs text-red-400">{errors.message}</p>
                    )}
                  </div>

                  <CardFooter className="px-0 pt-6">
                    <Button
                      type="submit"
                      className="w-full group"
                      disabled={isSubmitting || !user}
                    >
                      {isSubmitting
                        ? "Submitting..."
                        : "Send Inquiry to Sellers"}
                      {!isSubmitting && (
                        <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      )}
                    </Button>
                  </CardFooter>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default PartInquiryFormPage;
